import React, { useState, useEffect, useRef, useCallback } from "react";
import Markdown from "react-markdown";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import styles from "./SetupBoard.module.scss";
import Figure from "components/Figure/Figure";
import { Colors, Figures, FigureData } from "types";
import { create8x8Layout } from "services/Account/Supabase";
import { Session } from "@supabase/supabase-js";

interface SetupBoardProps {
    session?: Session
}

const SetupBoard: React.FC<SetupBoardProps> = (props) => {

    const roomsUrl = process.env.REACT_APP_HTTP_URL || '';

    const boardWidth = 8;
    const boardHeight = 2;
    const POINT_LIMIT = 64;

    const [boardState, setBoardState] = useState<{ [key: string]: FigureData | null }>({});
    const [draggingFigure, setDraggingFigure] = useState<FigureData | null>(null);
    const dragPosition = useRef<{ x: number; y: number } | null>(null);
    const [description, setDescription] = useState(`
This interface allows you to set up a custom chess board layout:
- ➕ Drag pieces from the left table to the board.
- ℹ️ Click on a piece to see what it does.
- ♻️ Move or swap pieces by dragging them to a new cell.
- ❌ Click the red ✕ button on a piece to remove it.
- 💾 Click Save to store your layout.
`);
    const [totalPoints, setTotalPoints] = useState(0);
    const [pieceData, setPieceData] = useState<{ [key: string]: number }>({});
    const [selectedPiece, setSelectedPiece] = useState<FigureData | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);


    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(
                "ontouchstart" in window || navigator.maxTouchPoints > 0
            );
        };

        checkIfMobile();
        window.addEventListener("resize", checkIfMobile);

        return () => window.removeEventListener("resize", checkIfMobile);
    }, []);

    const hasFetchedLayout = useRef(false);
    useEffect(() => {
        if (!props.session || hasFetchedLayout.current) return;

        hasFetchedLayout.current = true;
        const uid = props.session.user.id;

        const fetchPlayerLayout = async () => {
            try {

                const response = await fetch(`${roomsUrl}/player-layout?uid=${uid}`, {
                    method: "GET",
                    headers: new Headers({
                        "ngrok-skip-browser-warning": "69420",
                    })
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch player layout");
                }

                const data = await response.json();
                const layout = data.layout || [];
                const parsedLayout: { [key: string]: FigureData | null } = {};

                layout.pieces.forEach((piece: FigureData) => {
                    const key = `${piece.x},${piece.y}`;
                    parsedLayout[key] = piece;
                });

                setBoardState(parsedLayout);
            } catch (error) {
                console.error("Error loading player layout:", error);
                toast.error("Failed to load board layout. Please try again. If this is your first time making a deck, disregard.");
            }
        };

        fetchPlayerLayout();
    }, [props.session, roomsUrl]);

    const hasFetchedPieceData = useRef(false);
    useEffect(() => {
        if (!props.session || hasFetchedPieceData.current) return;
        hasFetchedPieceData.current = true;
        fetchPieceData();
    }, [props.session]);

    const fetchPieceData = async () => {
        try {
            const response = await fetch(`${roomsUrl}/piece-info?name=all`, {
                method: "GET",
                headers: new Headers({
                    "Accept": "application/json",
                    "ngrok-skip-browser-warning": "69420",
                })
            });
            if (!response.ok) throw new Error("Failed to fetch piece data");

            const data = await response.json();
            const pointLookup: { [key: string]: number } = {};

            data.pieces.forEach((piece: { name: string, points: number }) => {
                pointLookup[piece.name.toLowerCase().replace(' ', '').replace('_', '')] = piece.points;
            });

            setPieceData(pointLookup);
        } catch (error) {
            console.error("Error fetching piece data:", error);
            toast.error("Failed to load piece costs.");
        }
    };

    const calculateTotalPoints = () => {
        let sum = 0;
        Object.values(boardState).forEach(piece => {
            if (piece) {
                const pieceName = piece.name.toLowerCase().replace(' ', '').replace('_', '');
                if (pieceName in pieceData) {
                    sum += pieceData[pieceName];
                }
            }
        });

        setTotalPoints(sum);

        if (sum > POINT_LIMIT) {
            setWarningMessage(`⚠️ You are ${sum - POINT_LIMIT} points over the limit!`);
        } else {
            setWarningMessage(null);
        }
    };

    useEffect(() => {
        calculateTotalPoints();
    }, [boardState, pieceData]);

    const invertHeight = (coordinate: number) => boardHeight - 1 - coordinate;

    const fetchPieceInfo = async (pieceName: string) => {
        try {
            const response = await fetch(`${roomsUrl}/piece-info?name=${pieceName}`, {
                method: "GET",
                headers: new Headers({
                    "ngrok-skip-browser-warning": "69420",
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch info for piece: ${pieceName}`);
            }

            const data = await response.json();
            setDescription(`
# ${data.name} | ${data.points} points
*${data.tagline}*

${data.description}
            `);
        } catch (error) {
            console.error("Error fetching piece info:", error);
            setDescription(`
# Error
Failed to fetch information for the piece: **${pieceName}**.
            `);
        }
    };

    const handleFigureClick = (figure: FigureData) => {
        fetchPieceInfo(figure.name);
        setSelectedPiece(figure);
    };

    const handleDragStart = (
        event: React.DragEvent<HTMLDivElement>,
        figure: FigureData
    ) => {
        setDraggingFigure(figure);
        fetchPieceInfo(figure.name);
        if (isMobile) {
            setSelectedPiece(figure);
        }

        const svgElement = event.currentTarget.querySelector("svg");
        if (svgElement) {
            const rect = svgElement.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;

            // Create a transparent canvas
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");

            if (ctx) {
                ctx.clearRect(0, 0, width, height);
                const img = new Image();
                const svgData = new XMLSerializer().serializeToString(svgElement);
                const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
                const url = URL.createObjectURL(svgBlob);

                img.onload = () => {
                    ctx.drawImage(img, 0, 0);
                    URL.revokeObjectURL(url);
                    event.dataTransfer.setDragImage(canvas, width / 2, height / 2);
                };

                img.src = url;
            }
        }

        event.dataTransfer.setData("application/json", JSON.stringify(figure));
        event.dataTransfer.effectAllowed = "move";
    };


    const handleDragEnd = () => {
        if (draggingFigure) {
            setDraggingFigure(null);
        }
    };

    const removeSelectedPiece = () => {
        if (!selectedPiece) return;

        const key = `${selectedPiece.x},${selectedPiece.y}`;
        setBoardState((prev) => ({ ...prev, [key]: null }));
        setSelectedPiece(null);
    };

    const initCells = () => {
        const cells = [];
        for (let y = 0; y < boardHeight; y++) {
            for (let x = 0; x < boardWidth; x++) {
                const invertedX = x;
                const invertedY = invertHeight(y);
                const key = `${invertedX},${invertedY}`;

                const isDark = (x + y) % 2 === 1;
                const piece = boardState[key];
                const isSelected = selectedPiece?.x === invertedX && selectedPiece?.y === invertedY;

                cells.push(
                    <div
                        key={key}
                        className={`${styles.cell} ${isSelected ? styles.selectedCell : ""}`}
                        data-checkerboard={isDark ? "dark" : "light"}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleDrop(invertedX, invertedY)}
                    >
                        {piece && (
                            <div>
                                <Figure
                                    figure={piece}
                                    figureClicked={() => handleFigureClick(piece)}
                                    draggable
                                    onDragStart={(event) => handleDragStart(event, piece)}
                                    onDragEnd={handleDragEnd}
                                    relativePos
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                />
                                {!isMobile && (
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => removePiece(invertedX, invertedY)}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
        }
        return cells;
    };

    const handleDrop = (x: number, y: number) => {
        if (draggingFigure) {
            const key = `${x},${y}`;
            const prevKey = `${draggingFigure.x},${draggingFigure.y}`;

            setBoardState(prev => {
                const targetFigure = prev[key];
                let newState;
                let newFigData = { ...draggingFigure, x, y };
                if (draggingFigure.x === -1 || draggingFigure.y === -1) {
                    newState = {
                        ...prev,
                        [key]: newFigData,
                    };
                } else {
                    newState = {
                        ...prev,
                        [prevKey]: targetFigure ? { ...targetFigure, x: draggingFigure.x, y: draggingFigure.y } : null,
                        [key]: newFigData,
                    };
                }

                setSelectedPiece(newFigData);

                return newState;
            });

            setDraggingFigure(null);
        }
    };

    const removePiece = (x: number, y: number) => {
        const key = `${x},${y}`;
        setSelectedPiece(null);
        setBoardState(prev => ({
            ...prev,
            [key]: null,
        }));
    };

    const getCurrentLayout = () => {
        return Object.entries(boardState)
            .filter(([, figure]) => figure !== null && figure!.x >= 0 && figure!.y >= 0) // Ensure valid coordinates
            .map(([key, figure]) => ({
                position: key,
                name: figure!.name,
                x: figure!.x,
                y: figure!.y,
            }));
    };

    const saveSetup = async () => {
        const layout = getCurrentLayout();

        // Check if at least one King is present
        const hasKing = layout.some(piece => piece.name.toLowerCase().includes("king"));

        if (!hasKing) {
            toast.error("You must place at least one king on the board before saving!");
            return;
        }

        if (totalPoints > POINT_LIMIT) {
            toast.error(`Setup exceeds point limit by ${totalPoints - POINT_LIMIT} points!`);
            return;
        }

        console.log("Setup saved:", layout);

        try {
            await create8x8Layout(layout);
            toast.success("Setup saved successfully!");
        } catch (error) {
            console.error("Error saving setup:", error);
            toast.error("Failed to save setup. Please try again.");
        }
    };

    const updateDragPosition = useCallback((x: number, y: number) => {
        dragPosition.current = { x, y };
        requestAnimationFrame(() => {
            const draggedElem = document.getElementById("dragged-piece");
            if (draggedElem) {
                draggedElem.style.left = `${x - 36}px`;
                draggedElem.style.top = `${y - 36}px`;
            }
        });
    }, []);

    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>, figure: FigureData) => {
        setDraggingFigure(figure);
        updateDragPosition(event.touches[0].clientX, event.touches[0].clientY);

        document.body.style.overflow = "hidden"; // Disable page scrolling
        document.documentElement.style.overflow = "hidden";
        document.documentElement.style.touchAction = "none"; // Block scrolling and gestures
    };

    const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
        if (!draggingFigure) return;

        event.preventDefault(); // Blocks scrolling during drag
        updateDragPosition(event.touches[0].clientX, event.touches[0].clientY);
    };

    const boardRef = useRef<HTMLDivElement>(null);

    const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
        if (!draggingFigure || !dragPosition.current) return;
        if (!boardRef.current) return;

        const boardRect = boardRef.current.getBoundingClientRect();
        const cellWidth = boardRect.width / boardWidth;
        const cellHeight = boardRect.height / boardHeight;

        const touchX = dragPosition.current.x;
        const touchY = dragPosition.current.y;

        const x = Math.floor((touchX - boardRect.left) / cellWidth);
        const y = invertHeight(Math.floor((touchY - boardRect.top) / cellHeight));

        if (x >= 0 && x < boardWidth && y >= 0 && y < boardHeight) {
            handleDrop(x, y);
        }

        setDraggingFigure(null);
        dragPosition.current = null;

        // Restore scrolling
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
        document.documentElement.style.touchAction = "auto"; // Re-enable scrolling
    };



    const pieceTable = Object.values(Figures).map((figure) => {
        const newPiece: FigureData = {
            id: `${figure}-${Date.now()}`,
            name: figure,
            color: Colors.WHITE,
            x: -1,
            y: -1,
        };

        return (
            <div
                key={figure}
                className={styles.pieceTableItem}
            >
                <Figure
                    figure={newPiece}
                    draggable={!isMobile} // Only allow drag on non-touch devices
                    onDragStart={!isMobile ? (event) => handleDragStart(event, newPiece) : undefined}
                    onTouchStart={isMobile ? (event) => handleTouchStart(event, newPiece) : undefined}
                    onTouchMove={isMobile ? handleTouchMove : undefined}
                    onTouchEnd={isMobile ? handleTouchEnd : undefined}
                    figureClicked={() => fetchPieceInfo(figure)}
                    relativePos
                />
            </div>
        );
    });

    useEffect(() => {
        const pieceTable = document.querySelector(`.${styles.pieceTable}`) as HTMLElement | null;

        if (!pieceTable) return;

        const handleWheelScroll = (event: WheelEvent) => {
            if (event.deltaY !== 0) {
                event.preventDefault();
                pieceTable.scrollLeft -= event.deltaY;
            }
        };

        pieceTable.addEventListener("wheel", handleWheelScroll as EventListener, { passive: false });

        return () => pieceTable.removeEventListener("wheel", handleWheelScroll as EventListener);
    }, []);

    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 6; // Adjust based on screen size

    const paginatedPieces = Object.values(Figures).slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    ).map((figure) => {
        const newPiece: FigureData = {
            id: `${figure}-${Date.now()}`,
            name: figure,
            color: Colors.WHITE,
            x: -1,
            y: -1,
        };

        return (
            <div key={figure} className={styles.pieceTableItem}>
                <Figure
                    figure={newPiece}
                    draggable={!isMobile}
                    onDragStart={!isMobile ? (event) => handleDragStart(event, newPiece) : undefined}
                    onTouchStart={isMobile ? (event) => handleTouchStart(event, newPiece) : undefined}
                    onTouchMove={isMobile ? handleTouchMove : undefined}
                    onTouchEnd={isMobile ? handleTouchEnd : undefined}
                    figureClicked={() => fetchPieceInfo(figure)}
                    relativePos
                />
            </div>
        );
    });

    // Pagination Controls
    const totalPages = Math.ceil(Object.values(Figures).length / itemsPerPage);

    return (
        <div className="app">
            <div className={styles.setupBoardWrapper}>
                <ToastContainer />
                <div className={styles.description}>
                    <Markdown>{description}</Markdown>
                </div>

                <div className={styles.layout}>
                    <div className={styles.pieceTableWrapper}>
                        <div className={styles.pieceTable}>{paginatedPieces}</div>

                        {/* Ensure pagination buttons always show */}
                        <div className={styles.paginationControls}>
                            <button
                                disabled={currentPage === 0}
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                                className={styles.paginationButton}
                            >
                                ⬅️
                            </button>

                            <span className={styles.pageIndicator}>{currentPage + 1} / {totalPages}</span>

                            <button
                                disabled={currentPage === totalPages - 1}
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                                className={styles.paginationButton}
                            >
                                ➡️
                            </button>
                        </div>
                    </div>


                    <div className={styles.buttonBoard}>
                        <div className="w-full">
                            <div
                                className={styles.board}
                                ref={boardRef}
                                style={{
                                    gridTemplateColumns: `repeat(${boardWidth}, minmax(40px, 1fr))`,
                                    gridTemplateRows: `repeat(${boardHeight}, minmax(40px, 1fr))`,
                                    maxWidth: "100vw",
                                    minWidth: "320px",
                                    minHeight: "80px",
                                }}
                            >
                                {initCells()}
                                {draggingFigure && dragPosition.current && (
                                    <div
                                        id="dragged-piece"
                                        style={{
                                            position: "fixed",
                                            left: `${dragPosition.current.x - 36}px`,
                                            top: `${dragPosition.current.y - 36}px`,
                                            width: "72px",
                                            height: "72px",
                                            pointerEvents: "none",
                                            zIndex: 1000,
                                        }}
                                    >
                                        <Figure figure={draggingFigure} relativePos figureClicked={() => { }} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.buttonGroup}>
                            <button onClick={saveSetup} className={styles.saveSetupButton}>
                                Save
                            </button>
                            <div className={styles.pointCounterBox}>
                                <strong>Total Points:</strong>
                                <span className={`${styles.pointStatus} ${totalPoints > POINT_LIMIT ? styles.pointsOver : totalPoints === POINT_LIMIT ? styles.pointsExact : styles.pointsRemaining}`}>
                                    {totalPoints} / {POINT_LIMIT}
                                </span>
                                {warningMessage && <p className={styles.warningText}>{warningMessage}</p>}
                            </div>
                            {isMobile && selectedPiece && (
                                <button onClick={removeSelectedPiece} className={styles.removeSelectedButton}>
                                    Remove Selected Piece
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

};

export default SetupBoard;
